import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';
import WapiService from 'wanderu-service-wapi';
import { makeQueryString } from 'wanderu-utils';

import {
    actions as carrierActions,
    selectors as carrierDataSelectors,
} from '../../appstate';
import {
    FILTER_FIELDS,
    MIX_TRIP_CARRIER_CODE,
} from '../../constants';
import {
    namespace,
    actions,
    reducer,
    selectors,
} from './base';

const logger = Logger.getLogger('wanderu-ducks-carriers');

function fetchCarrier(carrierCode) {
    return async (dispatch, getState) => {
        let error;

        // A routed trip whose legs did not resolve renders with no carrier code at
        // all; asking for carriers.json/undefined files the result under the
        // duck's shared `default` bucket.
        if (!carrierCode || carrierCode === MIX_TRIP_CARRIER_CODE) {
            return;
        }

        // Having the data is the only reason to skip. This used to defer to the
        // full-catalog fetch instead, which is a trap now that the server render
        // ships only the carriers its results reference: a jitted search surfaces
        // carriers that render neither saw, and if the catalog request is slow or
        // fails, those cards mounted while it was in flight would never retry.
        if (carrierDataSelectors.selectCarrierData(getState(), carrierCode)) {
            return;
        }

        const hasFetched = selectors.selectSuccess(getState(), carrierCode);
        const isFetching = selectors.selectWorking(getState(), carrierCode);
        // Errors count as an answer. Cards remount on every direction toggle, so
        // without this a code wapi will not serve costs one request per card per
        // toggle for the rest of the session.
        const hasFailed = !!selectors.selectError(getState(), carrierCode);
        if (hasFetched || isFetching || hasFailed) {
            return;
        }

        dispatch(actions.startWork(carrierCode));

        try {
            const query = {
                fields: FILTER_FIELDS.join(','),
            };

            const carrierData = await WapiService.get(`carriers.json/${carrierCode}${makeQueryString(query)}`);

            if (!carrierData) {
                throw new Error('no carrier data found');
            }

            return dispatch(carrierActions.setCarrierData(carrierCode, carrierData.result));
        } catch (ex) {
            const domainUserInfo = appSettings.env.isClient
                ? SnowplowService.getDomainUserInfo()
                : {};

            logger.warn('fetchCarrier', ex, {
                snowplowIds: {
                    networkUserId: SnowplowService.getNetworkUserId(),
                    ...domainUserInfo,
                },
            });

            error = {
                message: ex.message,
            };
        } finally {
            dispatch(actions.endWork(carrierCode, undefined, error));
        }
    };
}

function setSuccess(carrierCode, meta) {
    return actions.endWork(carrierCode, meta);
}

const fetchCarrierAction = {
    fetchCarrier,
    setSuccess,
    ...selectors,
};

export {
    fetchCarrierAction,
};

export default {
    [namespace]: reducer,
};
